import { useState, useEffect } from "react";
import { Package, PaperPlaneTilt, ArrowDown, Clock, CheckCircle, Bluetooth, MapPin, Funnel, ChatCircleDots } from "@phosphor-icons/react";
import axios from "axios";
import RateShipment from "@/components/RateShipment";
import ShipmentChat from "@/components/ShipmentChat";
import RealtimeEtaCard from "@/components/RealtimeEtaCard";
import ShipmentMiniMap from "@/components/ShipmentMiniMap";
import PodViewerModal from "@/components/PodViewerModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  created: "Criada", bag_assigned: "Bolsa atribuida", awaiting_pickup: "A aguardar recolha",
  picked_up: "Recolhida", in_transit: "Em transito", at_delivery_point: "No ponto de entrega",
  delivered: "Entregue", awaiting_payment: "A aguardar pagamento",
};

export default function MyShipments() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [unread, setUnread] = useState({});  // {tracking_code: count}
  const [etaMap, setEtaMap] = useState({});  // {tracking_code: {progress_pct, remaining_minutes, eta_iso}}
  const [podViewTc, setPodViewTc] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem("blumove_token");
      if (!token) { setLoading(false); return; }
      try {
        const [shipRes, unreadRes] = await Promise.all([
          axios.get(`${API}/my-shipments`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/chat/unread-counts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { counts: {} } })),
        ]);
        setShipments(shipRes.data);
        setUnread(unreadRes.data?.counts || {});
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  // Poll realtime ETA every 8s for in-transit shipments only
  useEffect(() => {
    const inTransit = shipments.filter((s) => s.status === "in_transit");
    if (inTransit.length === 0) return;
    const fetchEtas = async () => {
      const results = await Promise.allSettled(
        inTransit.map((s) =>
          axios.get(`${API}/shipments/${s.tracking_code}/realtime-eta`).then((r) => r.data)
        )
      );
      const map = {};
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.in_simulation) {
          map[r.value.tracking_code] = r.value;
        }
      });
      setEtaMap(map);
    };
    fetchEtas();
    const id = setInterval(fetchEtas, 8000);
    return () => clearInterval(id);
  }, [shipments]);

  const handleExpand = async (trackingCode) => {
    const opening = expanded !== trackingCode;
    setExpanded(opening ? trackingCode : null);
    // Mark as read when opening
    if (opening && unread[trackingCode]) {
      const token = localStorage.getItem("blumove_token");
      try {
        await axios.post(`${API}/chat/${trackingCode}/read`, {},
          { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* ignore */ }
      setUnread(prev => {
        const next = { ...prev };
        delete next[trackingCode];
        return next;
      });
    }
  };

  const filtered = filter === "all" ? shipments
    : filter === "sent" ? shipments.filter(s => s.user_role_in_shipment === "sender")
    : filter === "received" ? shipments.filter(s => s.user_role_in_shipment === "receiver")
    : shipments.filter(s => s.status === filter);

  const counts = {
    all: shipments.length,
    sent: shipments.filter(s => s.user_role_in_shipment === "sender").length,
    received: shipments.filter(s => s.user_role_in_shipment === "receiver").length,
    in_transit: shipments.filter(s => s.status === "in_transit").length,
    delivered: shipments.filter(s => s.status === "delivered").length,
  };

  if (loading) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>A carregar envios...</p></div>;

  return (
    <div data-testid="my-shipments" className="pt-20 px-6 pb-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", marginBottom: 4 }}>Os meus envios</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Historico de encomendas enviadas e recebidas</p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6 animate-fade-in-up stagger-1" data-testid="shipment-filters">
          {[
            { id: "all", label: "Todos", icon: Package },
            { id: "sent", label: "Enviados", icon: PaperPlaneTilt },
            { id: "received", label: "Recebidos", icon: ArrowDown },
            { id: "in_transit", label: "Em transito", icon: Clock },
            { id: "delivered", label: "Entregues", icon: CheckCircle },
          ].map(f => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className="flex items-center gap-2"
              style={{
                padding: "8px 16px", borderRadius: 9999, border: "none",
                background: filter === f.id ? "var(--primary)" : "var(--surface)",
                color: filter === f.id ? "white" : "var(--text-muted)",
                fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                boxShadow: filter !== f.id ? "inset 0 0 0 1px var(--border-color)" : "none",
              }}
            >
              <f.icon size={14} weight={filter === f.id ? "fill" : "regular"} />
              {f.label}
              <span style={{ background: filter === f.id ? "rgba(255,255,255,0.2)" : "var(--bg)", padding: "1px 8px", borderRadius: 9999, fontSize: "0.7rem" }}>{counts[f.id] || 0}</span>
            </button>
          ))}
        </div>

        {/* Shipments list */}
        {filtered.length === 0 ? (
          <div className="bento-item p-12 text-center animate-fade-in">
            <Package size={48} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 12px" }} />
            <p style={{ fontFamily: "Outfit", fontWeight: 600, marginBottom: 4 }}>Nenhum envio encontrado</p>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
              {filter === "all" ? "Ainda nao tem envios registados." : "Nenhum envio com este filtro."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map((s, i) => {
              const isExpanded = expanded === s.tracking_code;
              const isSender = s.user_role_in_shipment === "sender";
              return (
                <div key={s.tracking_code || i} className={`bento-item overflow-hidden animate-fade-in-up`} style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }}>
                  <button
                    data-testid={`shipment-card-${s.tracking_code}`}
                    onClick={() => handleExpand(s.tracking_code)}
                    style={{ width: "100%", padding: "16px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: isSender ? "rgba(45,74,62,0.08)" : "rgba(37,99,235,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {isSender ? <PaperPlaneTilt size={20} weight="duotone" style={{ color: "var(--primary)" }} /> : <ArrowDown size={20} weight="duotone" style={{ color: "#2563eb" }} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.9rem", color: "var(--primary)" }}>{s.tracking_code}</span>
                            <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 9999, background: isSender ? "rgba(45,74,62,0.06)" : "rgba(37,99,235,0.06)", color: isSender ? "var(--primary)" : "#2563eb", fontWeight: 600 }}>
                              {isSender ? "Enviado" : "Recebido"}
                            </span>
                            {unread[s.tracking_code] > 0 && (
                              <span
                                data-testid={`unread-badge-${s.tracking_code}`}
                                className="flex items-center gap-1"
                                style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 9999, background: "var(--accent)", color: "white", fontWeight: 700 }}
                                title={`${unread[s.tracking_code]} mensagem(ns) por ler`}
                              >
                                <ChatCircleDots size={11} weight="fill" />
                                {unread[s.tracking_code]}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                            <span>{s.sender_name}</span>
                            <span>→</span>
                            <span>{s.recipient_name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {etaMap[s.tracking_code] && (
                          <span
                            data-testid={`realtime-eta-${s.tracking_code}`}
                            title={`Em transito · ${etaMap[s.tracking_code].progress_pct}% completo`}
                            className="flex items-center gap-1"
                            style={{ fontSize: "0.7rem", padding: "3px 10px", borderRadius: 9999, background: "rgba(37,99,235,0.08)", color: "#2563eb", fontWeight: 600 }}
                          >
                            <Clock size={11} weight="duotone" />
                            {etaMap[s.tracking_code].remaining_minutes < 1
                              ? "<1 min"
                              : etaMap[s.tracking_code].remaining_minutes < 60
                                ? `${Math.round(etaMap[s.tracking_code].remaining_minutes)} min`
                                : new Date(etaMap[s.tracking_code].eta_iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                        <span className={`status-${s.status}`} style={{ padding: "4px 12px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 600 }}>
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{ padding: "0 20px 20px", borderTop: "1px solid var(--border-color)" }} className="animate-fade-in">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-4">                        <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Recolha</span>
                          <p style={{ fontWeight: 600, fontSize: "0.8rem" }}>{s.pickup_point}</p>
                        </div>
                        <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Entrega</span>
                          <p style={{ fontWeight: 600, fontSize: "0.8rem" }}>{s.delivery_point}</p>
                        </div>
                        <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Bolsa</span>
                          <div className="flex items-center gap-1">
                            <Bluetooth size={12} style={{ color: "var(--primary)" }} />
                            <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.8rem" }}>{s.bag_code}</span>
                          </div>
                        </div>
                        <div style={{ padding: 10, borderRadius: 10, background: "var(--bg)" }}>
                          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600, display: "block" }}>Criado</span>
                          <p style={{ fontWeight: 600, fontSize: "0.8rem" }}>{new Date(s.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      </div>
                      {/* Timeline */}
                      {s.timeline && s.timeline.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {s.timeline.map((step, j) => (
                            <div key={j} className="flex items-center gap-3">
                              <div style={{ width: 20, height: 20, borderRadius: "50%", background: j === s.timeline.length - 1 ? "var(--primary)" : "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {j === s.timeline.length - 1 ? <div style={{ width: 6, height: 6, borderRadius: "50%", background: "white" }} /> : <CheckCircle size={12} weight="fill" color="white" />}
                              </div>
                              <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{step.description}</span>
                              <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginLeft: "auto" }}>
                                {new Date(step.timestamp).toLocaleString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Live ETA card (in_transit only) */}
                      <RealtimeEtaCard trackingCode={s.tracking_code} status={s.status} variant="card" />
                      {/* Live mini-map (in_transit only) */}
                      <ShipmentMiniMap trackingCode={s.tracking_code} status={s.status} />
                      {/* Proof of delivery (only if delivered) */}
                      {s.status === "delivered" && (
                        <button
                          data-testid={`view-pod-${s.tracking_code}`}
                          onClick={() => setPodViewTc(s.tracking_code)}
                          className="flex items-center gap-2"
                          style={{ marginTop: 12, padding: "9px 16px", borderRadius: 10, border: "1px solid var(--border-color)", background: "rgba(22,163,74,0.06)", color: "var(--success)", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem" }}
                        >
                          <CheckCircle size={15} weight="fill" /> Ver prova de entrega
                        </button>
                      )}
                      {/* Rate the shipment (only if delivered) */}
                      <RateShipment trackingCode={s.tracking_code} status={s.status} />
                      {/* Chat sender ↔ receiver (Item 5) */}
                      <ShipmentChat trackingCode={s.tracking_code} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {podViewTc && (
        <PodViewerModal trackingCode={podViewTc} onClose={() => setPodViewTc(null)} />
      )}
    </div>
  );
}
